import React, { useEffect, useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    Timestamp,
    where,
    writeBatch,
    updateDoc
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    IconButton,
    Stack,
    Pagination,
    Chip,
    InputAdornment,
    Tooltip,
    CircularProgress,
    Avatar,
    Grid,
    Button
} from "@mui/material";

import {
    Visibility,
    Close,
    Search as SearchIcon,
    HourglassEmpty,
    Payments,
    AccountBalanceWallet,
    PaidOutlined,
    PhoneIphone,
    MeetingRoomOutlined,
    ReceiptLong
} from "@mui/icons-material";

// Premium Theme Palette
const THEME = {
    BG_DARK: "#06362D",
    PANEL_DARK: "#06362D",
    DEEP_DARK: "#04241E",
    BORDER_DARK: "#D1A505",
    TEXT_PRIMARY: "#DEE2B1",
    TEXT_SECONDARY: "#D1A505",
    ACCENT_GOLD: "#FFD500",
    STRICT_DARK_BORDER: "rgba(0, 0, 0, 0.5)",
    DANGER: "#EF4444",
    DANGER_BG: "rgba(239, 68, 68, 0.15)",
    SUCCESS: "#10B981",
    SUCCESS_BG: "rgba(16, 185, 129, 0.15)",
    WARNING: "#F59E0B",
    WARNING_BG: "rgba(245, 158, 11, 0.15)"
};

function Pendingreservations() {
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        background: THEME.DEEP_DARK,
        color: THEME.TEXT_PRIMARY,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
    });

    const [bookings, setBookings] = useState([]);
    const [propertyId, setPropertyId] = useState(null);
    const [username, setUsername] = useState("");
    const [userData, setUserData] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);

    // 1️⃣ Auth and fetch user credentials
    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
            if (!user) {
                setLoading(false);
                return;
            }
            try {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) {
                    const data = snap.data();
                    setPropertyId(data.propertyId);
                    setUsername(data.username || data.name || "Staff");
                    setUserData({ uid: user.uid, ...data });
                }
            } catch (error) {
                console.error("Error fetching user role context:", error);
            } finally {
                setLoading(false);
            }
        });
        return () => unsubscribeAuth();
    }, []);

    // 2️⃣ Realtime Firestore listener for PENDING bookings only
    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, 'reservations'),
            where('propertyId', '==', propertyId),
            where('status', '==', 'Pending'),
            orderBy('checkindate', 'desc')
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const fetchedBookings = querySnapshot.docs.map(doc => ({
                id: doc.id,
                data: doc.data()
            }));
            setBookings(fetchedBookings);
        }, (error) => {
            console.error("Firestore subscription error:", error);

            // Fallback in case composite index is missing
            if (error.code === 'failed-precondition') {
                const fallbackQuery = query(
                    collection(db, 'reservations'),
                    where('propertyId', '==', propertyId),
                    where('status', '==', 'Pending')
                );

                return onSnapshot(fallbackQuery, (fallbackSnapshot) => {
                    const fetchedBookings = fallbackSnapshot.docs.map(doc => ({
                        id: doc.id,
                        data: doc.data()
                    })).sort((a, b) => new Date(a.data.checkindate) - new Date(b.data.checkindate));
                    setBookings(fetchedBookings);
                });
            }

            Toast.fire({
                icon: "error",
                title: "Failed to sync pending reservations."
            });
        });

        return unsubscribe;
    }, [propertyId]);

    // 3️⃣ Filter & Deduplicate pending bookings
    const processedBookings = useMemo(() => {
        const uniqueMap = new Map();

        bookings.forEach(item => {
            const custName = (item.data?.customers?.custname || "").trim().toLowerCase();
            const checkIn = item.data?.checkindate || "";
            const roomNo = (item.data?.rooms || []).map(r => r.roomno).sort().join(",");
            const totalAmount = item.data?.totalamount || 0;
            const uniqueKey = `${custName}_${checkIn}_${roomNo}_${totalAmount}`;

            if (!uniqueMap.has(uniqueKey)) {
                uniqueMap.set(uniqueKey, item);
            }
        });

        const deduplicated = Array.from(uniqueMap.values());

        return deduplicated.filter((item) => {
            const customerName = item.data?.customers?.custname || "";
            const mobileNo = item.data?.customers?.mobile || "";
            const bookingSource = item.data?.bookingsource || "";
            const targetRooms = (item.data?.rooms || []).map(r => r.roomno || "").join(" ");
            const combinedSearchString = `${customerName} ${mobileNo} ${bookingSource} ${targetRooms}`.toLowerCase();
            return combinedSearchString.includes(searchText.toLowerCase());
        });
    }, [bookings, searchText]);

    // Reset pagination on search
    useEffect(() => {
        setCurrentPage(1);
    }, [searchText]);

    // 4️⃣ Metrics for Pending Bookings
    const metrics = useMemo(() => {
        const totalCount = processedBookings.length;
        let totalBill = 0;
        let totalAdvance = 0;
        let totalBalance = 0;

        processedBookings.forEach(item => {
            const bill = Number(item.data?.totalamount) || 0;
            const advance = Number(item.data?.advance) || 0;
            const remaining = Number(item.data?.remaining) || (bill - advance);

            totalBill += bill;
            totalAdvance += advance;
            totalBalance += remaining;
        });

        return { totalCount, totalBill, totalAdvance, totalBalance };
    }, [processedBookings]);

    // Pagination slice
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = processedBookings.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(processedBookings.length / rowsPerPage);

    // 5️⃣ Transfer Reservation to "Paid"
    const handleTransferToPaid = async (bookingId, currentBookingData) => {
        if (!userData) {
            Toast.fire({ icon: "error", title: "Staff credentials missing." });
            return;
        }

        const remainingAmount = Number(currentBookingData?.remaining) || 0;
        const totalAmount = Number(currentBookingData?.totalamount) || 0;

        const result = await Swal.fire({
            title: 'Transfer to Paid?',
            html: `
                <div style="text-align: left; font-size: 0.9rem; color: ${THEME.TEXT_PRIMARY}">
                    <p>Mark this reservation status as <b>Paid</b> and clear pending balances.</p>
                    <p style="margin-top: 8px;"><b>Guest:</b> ${currentBookingData?.customers?.custname || "N/A"}</p>
                    <p><b>Total Bill:</b> ₹${totalAmount.toFixed(2)}</p>
                    <p><b>Pending Due:</b> ₹${remainingAmount.toFixed(2)}</p>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            background: THEME.DEEP_DARK,
            color: THEME.TEXT_PRIMARY,
            confirmButtonColor: THEME.SUCCESS,
            cancelButtonColor: THEME.STRICT_DARK_BORDER,
            confirmButtonText: 'Yes, Mark Paid',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            const batch = writeBatch(db);
            const bookingRef = doc(db, "reservations", bookingId);

            // Update main reservation to Paid
            batch.update(bookingRef, {
                status: "Booked",
                advance: totalAmount,
                remaining: 0,
                updatedAt: Timestamp.now(),
                lastUpdatedBy: username
            });

            // Update check-in details if any
            const checkinDetailsQuery = query(collection(db, 'checkindetails'), where('bookingid', '==', bookingId));
            const checkinDetailsSnapshot = await getDocs(checkinDetailsQuery);
            checkinDetailsSnapshot.docs.forEach(itemDoc => {
                batch.update(doc(db, "checkindetails", itemDoc.id), { status: "Paid" });
            });

            // Record transaction entry in payhistory
            const payHistoryRef = doc(collection(db, 'payhistory'));
            batch.set(payHistoryRef, {
                bookingid: bookingId,
                amountPaid: remainingAmount > 0 ? remainingAmount : totalAmount,
                paymentMode: "Direct Transfer to Paid",
                paymentDate: Timestamp.now(),
                receivedBy: username,
                propertyId: propertyId || "",
                status: "Paid"
            });

            // Activity Log
            const logRef = doc(collection(db, 'logs'));
            batch.set(logRef, {
                bookingid: bookingId,
                customerId: currentBookingData?.customerId || "",
                guestName: currentBookingData?.customers?.custname || "",
                action: "Status transitioned from Pending to Paid",
                staffUid: userData.uid,
                staffName: username,
                createdAt: Timestamp.now(),
                type: 'Booking Payment Update'
            });

            await batch.commit();

            Toast.fire({
                icon: "success",
                title: 'Reservation transitioned to Paid status!'
            });
        } catch (error) {
            console.error("Transfer to paid error:", error);
            Toast.fire({
                icon: "error",
                title: 'Failed to update reservation',
                text: error.message
            });
        }
    };

    // 6️⃣ Cancel Booking
    const cancelBooking = async (bookingId, bookingData) => {
        if (!userData) {
            Toast.fire({ icon: "error", title: "Action denied. Staff context required." });
            return;
        }

        const result = await Swal.fire({
            title: 'Cancel Pending Booking?',
            text: "This pending reservation will be marked as Canceled and its held inventory freed.",
            icon: 'warning',
            background: THEME.DEEP_DARK,
            color: THEME.TEXT_PRIMARY,
            showCancelButton: true,
            confirmButtonColor: THEME.DANGER,
            cancelButtonColor: THEME.STRICT_DARK_BORDER,
            confirmButtonText: 'Yes, Cancel Reservation'
        });

        if (!result.isConfirmed) return;

        try {
            const batch = writeBatch(db);
            const bookingRef = doc(db, "reservations", bookingId);

            batch.update(bookingRef, {
                status: "Canceled",
                canceledAt: Timestamp.now(),
                canceledBy: username
            });

            const checkinDetailsQuery = query(collection(db, 'checkindetails'), where('bookingid', '==', bookingId));
            const checkinDetailsSnapshot = await getDocs(checkinDetailsQuery);
            checkinDetailsSnapshot.docs.forEach(itemDoc => {
                batch.update(doc(db, "checkindetails", itemDoc.id), { status: "Canceled" });
            });

            const logRef = doc(collection(db, 'logs'));
            batch.set(logRef, {
                bookingid: bookingId,
                customerId: bookingData?.customerId || "",
                guestName: bookingData?.customers?.custname || "",
                uid: userData.uid,
                username: username,
                createdAt: Timestamp.now(),
                type: 'Pending Booking Cancellation'
            });

            await batch.commit();
            Toast.fire({ icon: "success", title: 'Reservation canceled successfully' });
        } catch (error) {
            console.error("Cancellation error:", error);
            Toast.fire({ icon: "error", title: 'Failed to cancel', text: error.message });
        }
    };

    return (
        <Box sx={{ display: "flex", backgroundColor: THEME.BG_DARK, minHeight: "100vh", color: THEME.TEXT_PRIMARY }}>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 2, md: 3.5 },
                    position: "relative",
                    backgroundColor: THEME.BG_DARK
                }}
            >
                {/* Header Section */}
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} mb={3}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <HourglassEmpty sx={{ color: THEME.WARNING, fontSize: 32 }} />
                            <Typography variant="h4" fontWeight={800} sx={{ color: THEME.TEXT_PRIMARY, letterSpacing: "-0.02em" }}>
                                Pending Reservations
                            </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, mt: 0.5 }}>
                            Review unconfirmed bookings, monitor pending dues, and transfer verified orders to Paid status
                        </Typography>
                    </Box>
                </Stack>


                {/* Main Management Card */}
                <Card sx={{ borderRadius: "16px", border: `1px solid ${THEME.BORDER_DARK}`, boxShadow: `0 8px 24px ${THEME.STRICT_DARK_BORDER}`, overflow: "hidden", backgroundColor: THEME.PANEL_DARK }}>
                    {/* Header Action Bar */}
                    <Box sx={{ p: 2.5, borderBottom: `1px solid ${THEME.BORDER_DARK}`, display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, gap: 2, backgroundColor: THEME.DEEP_DARK }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Chip 
                                label={`Pending Queue: ${processedBookings.length}`} 
                                sx={{
                                    backgroundColor: THEME.WARNING_BG,
                                    color: THEME.WARNING,
                                    border: `1px solid ${THEME.WARNING}`,
                                    fontWeight: 700,
                                    fontSize: "0.8rem"
                                }}
                            />
                        </Stack>

                        {/* Search Filter */}
                        <TextField
                            size="small"
                            placeholder="Search by guest, phone, room, or source..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: THEME.TEXT_SECONDARY, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: { xs: "100%", md: 380 },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: THEME.BG_DARK,
                                    color: THEME.TEXT_PRIMARY,
                                    fontSize: "0.85rem",
                                    border: `1px solid ${THEME.BORDER_DARK}`,
                                    "& fieldset": { border: "none" }
                                }
                            }}
                        />
                    </Box>

                    {/* Table View */}
                    <TableContainer sx={{ maxHeight: "calc(100vh - 380px)", minHeight: 360, backgroundColor: THEME.PANEL_DARK }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {["#", "Actions", "Transfer to Paid", "Guest Details", "Channel", "Dates", "Room(s)", "Plan", "Guests", "Total Bill", "Advance", "Remaining Due"].map((header, idx) => (
                                        <TableCell
                                            key={header}
                                            align={idx >= 9 ? "right" : "left"}
                                            sx={{
                                                backgroundColor: THEME.DEEP_DARK,
                                                color: THEME.TEXT_SECONDARY,
                                                fontWeight: 800,
                                                fontSize: "0.75rem",
                                                borderBottom: `1px solid ${THEME.BORDER_DARK}`,
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center" sx={{ py: 8, borderBottom: "none" }}>
                                            <CircularProgress size={36} sx={{ color: THEME.ACCENT_GOLD }} />
                                            <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, mt: 1.5 }}>
                                                Loading pending bookings...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : currentRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center" sx={{ py: 8, borderBottom: "none" }}>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                                                <MeetingRoomOutlined sx={{ fontSize: 44, color: THEME.BORDER_DARK }} />
                                                <Typography variant="body2" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600 }}>
                                                    No pending reservations found.
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    currentRows.map((item, index) => {
                                        return (
                                            <TableRow
                                                key={item.id}
                                                hover
                                                sx={{
                                                    backgroundColor: THEME.PANEL_DARK,
                                                    "&:hover": {
                                                        backgroundColor: `${THEME.DEEP_DARK} !important`
                                                    },
                                                    "& td": { borderBottom: `1px solid rgba(209, 165, 5, 0.2)` }
                                                }}
                                            >
                                                {/* Serial Number */}
                                                <TableCell sx={{ fontSize: "0.78rem", color: THEME.TEXT_SECONDARY, fontWeight: 600 }}>
                                                    {indexOfFirstRow + index + 1}
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell>
                                                    <Stack direction="row" spacing={0.6}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                href={`/reservation-details/${item.id}`}
                                                                sx={{
                                                                    p: 0.6,
                                                                    borderRadius: "8px",
                                                                    backgroundColor: THEME.DEEP_DARK,
                                                                    color: THEME.TEXT_PRIMARY,
                                                                    border: `1px solid ${THEME.BORDER_DARK}`,
                                                                    "&:hover": { backgroundColor: THEME.BG_DARK, color: THEME.ACCENT_GOLD }
                                                                }}
                                                            >
                                                                <Visibility sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Cancel Booking">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => cancelBooking(item.id, item.data)}
                                                                sx={{
                                                                    p: 0.6,
                                                                    borderRadius: "8px",
                                                                    backgroundColor: THEME.DANGER_BG,
                                                                    color: THEME.DANGER,
                                                                    border: `1px solid ${THEME.DANGER}`,
                                                                    "&:hover": { backgroundColor: THEME.DANGER, color: "#ffffff" }
                                                                }}
                                                            >
                                                                <Close sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>

                                                {/* Transfer to Paid Button */}
                                                <TableCell>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<PaidOutlined sx={{ fontSize: "16px !important" }} />}
                                                        onClick={() => handleTransferToPaid(item.id, item.data)}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontWeight: 700,
                                                            fontSize: "0.72rem",
                                                            py: 0.5,
                                                            px: 1.5,
                                                            borderRadius: "6px",
                                                            backgroundColor: THEME.SUCCESS_BG,
                                                            color: THEME.SUCCESS,
                                                            border: `1px solid ${THEME.SUCCESS}`,
                                                            boxShadow: "none",
                                                            "&:hover": {
                                                                backgroundColor: THEME.SUCCESS,
                                                                color: THEME.DEEP_DARK,
                                                                boxShadow: `0 2px 8px ${THEME.SUCCESS_BG}`
                                                            }
                                                        }}
                                                    >
                                                        Mark as Paid
                                                    </Button>
                                                </TableCell>

                                                {/* Guest Details */}
                                                <TableCell>
                                                    <Box>
                                                        <Typography fontWeight={700} sx={{ color: THEME.TEXT_PRIMARY, fontSize: "0.83rem" }}>
                                                            {item.data?.customers?.custname || "—"}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.72rem" }}>
                                                            <PhoneIphone sx={{ fontSize: 13 }} />
                                                            {item.data?.customers?.mobile || "No Contact"}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* Channel */}
                                                <TableCell>
                                                    <Chip
                                                        label={item.data?.bookingsource || "Direct"}
                                                        size="small"
                                                        sx={{
                                                            fontSize: "0.7rem",
                                                            fontWeight: 600,
                                                            backgroundColor: THEME.DEEP_DARK,
                                                            border: `1px solid ${THEME.BORDER_DARK}`,
                                                            color: THEME.TEXT_PRIMARY
                                                        }}
                                                    />
                                                </TableCell>

                                                {/* Dates */}
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ display: "block", color: THEME.SUCCESS, fontWeight: 700, fontSize: "0.72rem" }}>
                                                            IN: {item.data?.checkindate ? moment(item.data.checkindate).format("DD MMM, YYYY") : "—"}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: "block", color: THEME.TEXT_SECONDARY, fontWeight: 700, fontSize: "0.72rem" }}>
                                                            OUT: {item.data?.checkoutdate ? moment(item.data.checkoutdate).format("DD MMM, YYYY") : "—"}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* Rooms */}
                                                <TableCell>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {(item.data?.rooms || []).map((r, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={`Room ${r.roomno}`}
                                                                size="small"
                                                                sx={{
                                                                    height: "22px",
                                                                    fontSize: "0.7rem",
                                                                    fontWeight: 700,
                                                                    backgroundColor: THEME.DEEP_DARK,
                                                                    color: THEME.TEXT_PRIMARY,
                                                                    border: `1px solid ${THEME.BORDER_DARK}`
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </TableCell>

                                                {/* Plan */}
                                                <TableCell>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {(item.data?.rooms || []).map((r, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={r.planname || "EP"}
                                                                size="small"
                                                                sx={{
                                                                    height: "22px",
                                                                    fontSize: "0.7rem",
                                                                    fontWeight: 600,
                                                                    backgroundColor: THEME.DEEP_DARK,
                                                                    color: THEME.TEXT_SECONDARY,
                                                                    border: `1px solid ${THEME.BORDER_DARK}`
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </TableCell>

                                                {/* Guests */}
                                                <TableCell sx={{ fontSize: "0.8rem", color: THEME.TEXT_PRIMARY, fontWeight: 600 }}>
                                                    {item.data?.adults || 1}
                                                </TableCell>

                                                {/* Total Bill */}
                                                <TableCell align="right" sx={{ fontSize: "0.82rem", fontWeight: 700, color: THEME.TEXT_PRIMARY }}>
                                                    ₹ {(Number(item.data?.totalamount) || 0).toFixed(2)}
                                                </TableCell>

                                                {/* Advance */}
                                                <TableCell align="right" sx={{ fontSize: "0.82rem", fontWeight: 700, color: THEME.SUCCESS }}>
                                                    ₹ {(Number(item.data?.advance) || 0).toFixed(2)}
                                                </TableCell>

                                                {/* Remaining Due */}
                                                <TableCell align="right" sx={{ fontSize: "0.82rem", fontWeight: 800, color: (Number(item.data?.remaining) || 0) > 0 ? THEME.DANGER : THEME.TEXT_SECONDARY }}>
                                                    ₹ {(Number(item.data?.remaining) || 0).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination Footer */}
                    {totalPages > 1 && (
                        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${THEME.BORDER_DARK}`, backgroundColor: THEME.DEEP_DARK }}>
                            <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY }}>
                                Showing {indexOfFirstRow + 1}–{Math.min(indexOfLastRow, processedBookings.length)} of {processedBookings.length} pending items
                            </Typography>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(e, v) => setCurrentPage(v)}
                                shape="rounded"
                                size="small"
                                sx={{
                                    "& .MuiPaginationItem-root": {
                                        color: THEME.TEXT_PRIMARY,
                                        borderColor: THEME.BORDER_DARK
                                    },
                                    "& .Mui-selected": {
                                        backgroundColor: `${THEME.BORDER_DARK} !important`,
                                        color: `${THEME.DEEP_DARK} !important`,
                                        fontWeight: 800
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Card>
            </Box>
        </Box>
    );
}

export default Pendingreservations;